/**
 * WidgetPicker — a side panel listing the widgets a person can add, grouped by category with search.
 * Demo pages: /file-manager (tile list with search), /ui-elements/dialogs. Widget definitions come from the generated
 * `listWidgets` query. Adding is gated by the caller (`CanManageDashboards`).
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { SearchBar } from '../../components/ui/SearchBar.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { Skeleton } from '../../components/ui/Skeleton.js';
import { useWidgetDefinitions } from '../../hooks/useWorkflowOutputs.js';
import type { WorkflowOutputWidgetDefinition } from '../../generated/api/types.gen.js';

interface WidgetPickerProps {
  open: boolean;
  onClose: () => void;
  onAdd: (widgetDefinitionId: number) => void;
  adding?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  trading: 'Trading',
  research: 'Research',
  risk: 'Risk',
  portfolio: 'Portfolio',
  other: 'Other',
};

const CATEGORY_ORDER = ['trading', 'research', 'risk', 'portfolio'];

export const WidgetPicker: React.FC<WidgetPickerProps> = ({ open, onClose, onAdd, adding = false }) => {
  const query = useWidgetDefinitions();
  const [search, setSearch] = useState('');

  // Only user-eligible (non-system) widget definitions: system widgets are reserved for platform dashboards
  // (spec §6 H2).
  const widgets: WorkflowOutputWidgetDefinition[] = (query.data?.data ?? []).filter((w) => !w.isSystem);
  const needle = search.trim().toLowerCase();
  const filtered = needle
    ? widgets.filter(
        (w) =>
          w.name.toLowerCase().includes(needle) ||
          (w.description ?? '').toLowerCase().includes(needle) ||
          (w.category ?? '').toLowerCase().includes(needle),
      )
    : widgets;

  const grouped = new Map<string, WorkflowOutputWidgetDefinition[]>();
  for (const w of filtered) {
    const cat = w.category ?? 'other';
    grouped.set(cat, [...(grouped.get(cat) ?? []), w]);
  }
  const categories = [...grouped.keys()].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Add a widget"
      description="Pick what this dashboard should show."
      width="md"
    >
      <div className="space-y-5">
        <SearchBar value={search} onChange={setSearch} placeholder="Search widgets…" className="sm:w-full" />

        {query.isPending && (
          <div className="space-y-3" role="status" aria-label="Loading widgets">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={56} rounded="rounded-lg" />
            ))}
          </div>
        )}
        {query.isError && (
          <Callout tone="danger" title="Could not load widgets">
            {query.error.message}
          </Callout>
        )}
        {!query.isPending && !query.isError && filtered.length === 0 && (
          <p className="py-6 text-center text-sm text-muted">
            {widgets.length === 0 ? 'No widgets are available yet.' : 'No widgets match your search.'}
          </p>
        )}

        {categories.map((cat) => (
          <section key={cat}>
            <h3 className="mb-2 text-xs font-semibold tracking-wider text-muted uppercase">
              {CATEGORY_LABELS[cat] ?? cat}
            </h3>
            <ul className="space-y-2">
              {(grouped.get(cat) ?? []).map((w) => (
                <li
                  key={w.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-card-border p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-heading">{w.name}</p>
                    {w.description && <p className="mt-0.5 truncate text-xs text-muted">{w.description}</p>}
                  </div>
                  <Button variant="secondary" size="sm" loading={adding} onClick={() => onAdd(w.id)}>
                    <Plus size={14} aria-hidden="true" />
                    Add
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Sheet>
  );
};
